// Per-location detail. Phase 3a: renders location identity + owner (subscriber)
// + attached SIP accounts + the devices installed there, and lets org_admin edit address,
// assign/unassign the subscriber owner, and soft-delete once preconditions
// pass.
//
// Constraint: a location's SIP accounts and devices must belong to the same
// subscriber as the location itself (see project_verso_data_constraints.md).
// The server enforces this — the UI just surfaces the current state.

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useBackTo from '../hooks/useBackTo.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fmtDate, fmtDateTime } from '../lib/time.js';
import { useAuth } from '../hooks/useAuth.js';
import { canOperate, canAdmin } from '../lib/roles.js';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import E911SiteCard from '../components/locations/E911SiteCard.jsx';
import LocationMap from '../components/locations/LocationMap.jsx';

const TYPE_LABEL = { business: 'Business', residential: 'Residential' };

// A local type-delete dialog used to live here. It was dead code — locations are
// never deleted, only marked inactive (#205) — and components/shared covers the
// pattern if a real delete ever lands.

// ------- Section wrapper -------
function Section({ title, subtitle, children, actions }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value ?? <span className="text-gray-300">—</span>}</dd>
    </div>
  );
}

function TypePill({ type }) {
  const label = TYPE_LABEL[type] || type || '—';
  const cls = type === 'business'
    ? 'bg-blue-100 text-blue-700'
    : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

// ------- Edit / Create address modal -------
export function LocationFormModal({ initial, title, submitLabel, onSubmit, onCancel, isPending, error }) {
  const [label, setLabel]               = useState(initial?.label ?? '');
  const [addressLine1, setAddressLine1] = useState(initial?.addressLine1 ?? initial?.address_line1 ?? '');
  const [addressLine2, setAddressLine2] = useState(initial?.addressLine2 ?? initial?.address_line2 ?? '');
  const [city, setCity]                 = useState(initial?.city ?? '');
  const [state, setState]               = useState(initial?.state ?? '');
  const [postal, setPostal]             = useState(initial?.postal ?? '');
  const [country, setCountry]           = useState(initial?.country ?? 'US');
  const [type, setType]                 = useState(initial?.type || 'residential');
  const [validating, setValidating]     = useState(false);
  const [validation, setValidation]     = useState(null); // { ok, text }

  const validate = async () => {
    setValidating(true);
    setValidation(null);
    try {
      const res = await api.post('/locations/geocode', {
        address_line1: addressLine1.trim(), address_line2: addressLine2.trim(),
        city: city.trim(), state: state.trim(), postal: postal.trim(), country: country.trim(),
      });
      if (res.ok) {
        setValidation({ ok: true, text: `✓ ${res.formatted}${res.partialMatch ? ' (partial match — double-check)' : ''}` });
      } else {
        setValidation({ ok: false, text: res.error || `Not found (${res.status})` });
      }
    } catch (err) {
      setValidation({ ok: false, text: err?.body?.error || err?.message || 'Validation failed' });
    } finally {
      setValidating(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      label: label.trim() || null,
      address_line1: addressLine1.trim() || null,
      address_line2: addressLine2.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
      postal: postal.trim() || null,
      country: country.trim() || null,
      type,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              autoFocus
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Main office"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="business">Business</option>
              <option value="residential">Residential</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Street 1</label>
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Street 2</label>
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">State</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Postal</label>
              <input
                type="text"
                value={postal}
                onChange={(e) => setPostal(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Country</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          {validation && (
            <p className={`rounded-md border px-3 py-2 text-xs ${validation.ok ? 'border-green-200 bg-green-50 text-green-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              {validation.text}
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={validate}
              disabled={validating || !addressLine1.trim()}
              className="mr-auto rounded-md border border-blue-300 px-4 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
            >
              {validating ? 'Validating…' : 'Validate address'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Saving...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function LocationDetail() {
  // Where the arrow returns to — the screen the operator left, not this object's
  // global list. Falls back to the list when nothing was passed. See hooks/useBackTo.js.
  const back = useBackTo('Locations', '/locations');
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canOp  = canOperate(role);  // subscriber assign/unassign
  const canAdm = canAdmin(role);    // edit metadata, delete

  const locQ = useQuery({
    queryKey: ['location', id],
    queryFn: () => api.get(`/locations/${id}`),
  });

  const [editing, setEditing] = useState(false);
  const [geoMsg, setGeoMsg] = useState(null);
  const [e911Live, setE911Live] = useState(null);

  const refreshE911 = useMutation({
    mutationFn: () => api.get(`/locations/${id}/e911`),
    onSuccess: (res) => setE911Live({ ok: true, ...res }),
    onError: (err) => setE911Live({ ok: false, error: err?.body?.error || err?.message || 'Commio read failed' }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['location', id] });
    queryClient.invalidateQueries({ queryKey: ['locations'] });
  };

  const patchLoc = useMutation({
    mutationFn: (body) => api.patch(`/locations/${id}`, body),
    onSuccess: () => { invalidate(); setEditing(false); },
  });

  // Locations are never deleted — "Mark inactive" flips status. A location's
  // status otherwise follows its linked subscriber; inactive is the resting
  // state for an unassigned/decommissioned address.
  const markInactive = useMutation({
    mutationFn: () => api.patch(`/locations/${id}`, { status: 'inactive' }),
    onSuccess: () => invalidate(),
  });
  // Clear the override so status reverts to the linked subscriber's (or inactive
  // when unassigned).
  // SETS the override rather than clearing it. Clearing means "derive from the tenants",
  // and an address with no tenant derives to inactive — so on a location with none (every
  // E911-only site: 445 Carter Dr, 920 McCall Alley, 430 Main St, 10 Eagle Nest Lane) the
  // button cleared the override, the status stayed inactive, and it looked broken. Kevin,
  // 2026-08-10: "I am not able to reactivate the location that is linked to the e911 site."
  //
  // Active is the mirror of "Mark inactive": both are the operator saying so explicitly.
  const reactivate = useMutation({
    mutationFn: () => api.patch(`/locations/${id}`, { status_override: 'active' }),
    onSuccess: () => invalidate(),
  });

  // --- Who is at this address ---
  //
  // A location can serve several customers (0032). Only same-TYPE customers can
  // be added: Commio's E911 site carries location_type and it must agree with
  // the DID's CNAM type, so a business and a residence need separate rows at the
  // same street address.
  const [addingSub, setAddingSub] = useState(false);
  const [pickedSubId, setPickedSubId] = useState('');
  const [linkErr, setLinkErr] = useState(null);

  const custQ = useQuery({
    queryKey: ['subscribers', 'for-location-link'],
    queryFn: () => api.get('/subscribers'),
    enabled: addingSub,
  });

  const linkSub = useMutation({
    mutationFn: (subId) => api.put(`/locations/${id}/subscriber/${subId}`),
    onSuccess: () => {
      setLinkErr(null); setAddingSub(false); setPickedSubId('');
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['subscriber'] });
    },
    onError: (err) => setLinkErr(err?.body?.error || err?.message || 'Could not add that customer.'),
  });

  const unlinkSub = useMutation({
    mutationFn: (subId) => api.delete(`/locations/${id}/subscriber/${subId}`),
    onSuccess: () => {
      setLinkErr(null);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['subscriber'] });
    },
    onError: (err) => setLinkErr(err?.body?.error || err?.message || 'Could not remove that customer.'),
  });

  // Google geocode: validate + resolve the stored address to lat/lon.
  const geocode = useMutation({
    mutationFn: () => api.post(`/locations/${id}/geocode`, {}),
    onSuccess: (res) => {
      invalidate();
      setGeoMsg({ ok: true, text: `Geocoded${res?.partialMatch ? ' (partial match — verify the pin)' : ''}: ${res?.formatted || ''}` });
    },
    onError: (err) => setGeoMsg({ ok: false, text: err?.body?.error || err?.message || 'Geocoding failed' }),
  });

  // Manual pin drag → persist adjusted coordinates.
  const setPin = useMutation({
    mutationFn: ({ lat, lon }) => api.put(`/locations/${id}/pin`, { lat, lon }),
    onSuccess: () => invalidate(),
  });

  const loc = locQ.data;

  const breadcrumb = (
    <div className="text-sm text-gray-500">
      <Link to={back.href} className="text-blue-600 hover:underline">{back.label}</Link>
      <span className="mx-2">/</span>
      <span>{loc?.label || `#${id}`}</span>
    </div>
  );

  if (locQ.isLoading) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <LoadingSpinner message="Loading location..." />
      </div>
    );
  }
  if (locQ.error) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {locQ.error?.body?.error || locQ.error.message || 'Failed to load location'}
        </div>
      </div>
    );
  }
  if (!loc) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <div className="rounded-md border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Location not found.
        </div>
      </div>
    );
  }

  // Every customer at this address, primary first. This page is where the names
  // live — list views elsewhere show a count and link here.
  const linkedSubs = Array.isArray(loc.subscribers) ? loc.subscribers : [];
  const locType = loc.type || 'residential';
  const linkedIds = new Set(linkedSubs.map((s) => Number(s.id)));
  const allCustomers = Array.isArray(custQ.data)
    ? custQ.data
    : Array.isArray(custQ.data?.rows) ? custQ.data.rows : [];
  // Only offer customers who could legally share this E911 site.
  const eligibleCustomers = allCustomers.filter(
    (c) => !linkedIds.has(Number(c.id)) && (c.type || 'residential') === locType,
  );
  const addressLine1 = loc.addressLine1 || loc.address_line1;
  const addressLine2 = loc.addressLine2 || loc.address_line2;
  const addressFull  = [addressLine1, addressLine2, loc.city, loc.state, loc.postal, loc.country]
    .filter(Boolean).join(', ');
  const erlId = loc.erlId || loc.erl_id;
  // E911 registered = a DID's E911 uses this location (server-derived), or an
  // enterprise ERL id is present. erlID alone was the old (wrong) signal.
  const e911Registered = loc.e911Registered ?? Boolean(erlId);
  const commioLocId = loc.commioLocationId ?? loc.commio_location_id;
  const formatted = loc.formattedAddress || loc.formatted_address;
  const lat = loc.lat != null ? Number(loc.lat) : null;
  const lon = loc.lon != null ? Number(loc.lon) : null;
  // Several devices can share an address — a phone per desk, an ATA in a closet,
  // the router itself. `loc.device` is the legacy first-only field; read the list.
  const devices = Array.isArray(loc.devices) ? loc.devices : (loc.device ? [loc.device] : []);

  return (
    <div className="space-y-6">
      {breadcrumb}

      {/* Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">
                {loc.label || `Location #${loc.id}`}
              </h1>
              <TypePill type={loc.type} />
              {e911Registered
                ? <StatusBadge status="active" label="E911 registered" />
                : <StatusBadge status="inactive" label="E911 unregistered" />}
              {(() => {
                const eff = loc.effectiveStatus || loc.status || 'inactive';
                return <StatusBadge status={eff === 'active' ? 'active' : 'inactive'} label={eff} />;
              })()}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {addressFull || <span className="text-gray-400">no address on file</span>}
            </p>
          </div>
          {canAdm && (
            <div className="flex flex-shrink-0 gap-2">
              <button
                onClick={() => { setEditing(true); patchLoc.reset(); }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Edit
              </button>
              {(loc.effectiveStatus || loc.status) !== 'inactive' ? (
                <button
                  onClick={() => markInactive.mutate()}
                  disabled={markInactive.isPending}
                  title="Locations are never deleted — this marks the address inactive."
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {markInactive.isPending ? 'Working…' : 'Mark inactive'}
                </button>
              ) : (
                <button
                  onClick={() => reactivate.mutate()}
                  disabled={reactivate.isPending}
                  title="Mark this address active. An address with no customer does not become active on its own."
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {reactivate.isPending ? 'Working…' : 'Reactivate'}
                </button>
              )}
            </div>
          )}
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="Street 1" value={addressLine1} />
          <Field label="Street 2" value={addressLine2} />
          <Field label="City"     value={loc.city} />
          <Field label="State"    value={loc.state} />
          <Field label="Postal"   value={loc.postal} />
          <Field label="Country"  value={loc.country} />
          <Field label="ERL ID"   value={erlId ? <span className="font-mono">{erlId}</span> : null} />
          <Field label="Created"  value={fmtDate(loc.createdAt || loc.created_at)} />
        </dl>
      </div>

      {/* The carrier's 911 registration. Directly under the header because it is the
          field that decides whether a line here can be built at all — and, when it is
          wrong, where an ambulance goes. */}
      <E911SiteCard location={loc} canEdit={canAdm} />

      {/* Who is served at this address */}
      <Section
        title="Customers at this address"
        subtitle={
          linkedSubs.length > 1
            ? 'More than one customer is served here. Each keeps its own numbers, extensions and devices — only the dispatch address is shared.'
            : 'Who this service address belongs to.'
        }
        actions={canOp && !addingSub && (
          <button
            onClick={() => { setAddingSub(true); setLinkErr(null); }}
            className="rounded-md border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
          >
            Add customer
          </button>
        )}
      >
        {addingSub && (
          <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
            <label className="block text-xs font-medium text-gray-700">
              {locType === 'business' ? 'Business' : 'Residential'} customer to add
            </label>
            <p className="mt-0.5 text-xs text-gray-500">
              Only {locType} customers are listed. Commio requires this address&rsquo;s E911 record to match
              the caller-ID type, so a {locType === 'business' ? 'residential' : 'business'} customer needs
              its own address entry here instead.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select
                value={pickedSubId}
                onChange={(e) => setPickedSubId(e.target.value)}
                className="min-w-64 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              >
                <option value="">
                  {custQ.isLoading ? 'Loading…' : `— pick a ${locType} customer —`}
                </option>
                {eligibleCustomers.map((c) => (
                  <option key={c.id} value={c.id}>{c.display_name || c.name || `#${c.id}`}</option>
                ))}
              </select>
              <button
                onClick={() => linkSub.mutate(pickedSubId)}
                disabled={!pickedSubId || linkSub.isPending}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {linkSub.isPending ? 'Adding…' : 'Add'}
              </button>
              <button
                onClick={() => { setAddingSub(false); setPickedSubId(''); setLinkErr(null); }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
            {!custQ.isLoading && eligibleCustomers.length === 0 && (
              <p className="mt-2 text-xs text-gray-500">
                No other {locType} customers available to add.
              </p>
            )}
          </div>
        )}

        {linkErr && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{linkErr}</p>
        )}

        {linkedSubs.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">
            No customer is linked to this address yet — it&rsquo;s in the org pool.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100">
            {linkedSubs.map((sub) => (
              <li key={sub.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <Link to={`/subscribers/${sub.id}`} className="text-sm font-medium text-blue-600 hover:underline">
                    {sub.name || `Customer #${sub.id}`}
                  </Link>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <TypePill type={sub.type} />
                    <StatusBadge status={sub.status === 'active' ? 'active' : 'inactive'} label={sub.status || 'unknown'} />
                    {sub.isPrimary && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600">primary</span>
                    )}
                  </div>
                </div>
                {canOp && (
                  <button
                    onClick={() => { setLinkErr(null); unlinkSub.mutate(sub.id); }}
                    disabled={unlinkSub.isPending}
                    title="Remove this customer from the address. Other customers here keep it."
                    className="flex-shrink-0 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Map + geocode */}
      <Section
        title="Map"
        subtitle="Validate the address against Google Maps and drag the pin to set the exact service location."
        actions={canOp && (
          <button
            onClick={() => { setGeoMsg(null); geocode.mutate(); }}
            disabled={geocode.isPending}
            className="rounded-md border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            {geocode.isPending ? 'Geocoding…' : 'Validate & geocode'}
          </button>
        )}
      >
        {geoMsg && (
          <p className={`mt-3 rounded-md border px-3 py-2 text-xs ${geoMsg.ok ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
            {geoMsg.text}
          </p>
        )}
        <div className="mt-3">
          <LocationMap
            lat={lat}
            lon={lon}
            interactive={canOp}
            onPinChange={(newLat, newLon) => setPin.mutate({ lat: newLat, lon: newLon })}
          />
        </div>
        {(lat != null && lon != null) && (
          <p className="mt-2 font-mono text-xs text-gray-500">
            {lat.toFixed(6)}, {lon.toFixed(6)}
            {loc.geocodedAt || loc.geocoded_at ? ` · updated ${fmtDateTime(loc.geocodedAt || loc.geocoded_at)}` : ''}
          </p>
        )}
      </Section>

      {/* E911 (Commio) */}
      <Section
        title="E911"
        subtitle="Emergency dispatch registration for this address at the carrier (Commio)."
        actions={commioLocId && (
          <button
            onClick={() => { setE911Live(null); refreshE911.mutate(); }}
            disabled={refreshE911.isPending}
            className="rounded-md border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          >
            {refreshE911.isPending ? 'Reading…' : 'Refresh from Commio'}
          </button>
        )}
      >
        <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field label="E911 status" value={e911Registered
            ? <StatusBadge status="active" label="Registered" />
            : <StatusBadge status="inactive" label="Not registered" />} />
          <Field label="ERL ID" value={erlId ? <span className="font-mono">{erlId}</span> : null} />
          <Field label="Commio location ID" value={commioLocId ? <span className="font-mono">{commioLocId}</span> : null} />
          <Field label="Verso UUID" value={(loc.versoUuid || loc.verso_uuid) ? <span className="font-mono text-xs">{loc.versoUuid || loc.verso_uuid}</span> : null} />
          <Field label="Validated address" value={formatted} />
        </dl>
        {!commioLocId && (
          <p className="mt-3 text-xs text-gray-400">Not linked to a Commio E911 site yet.</p>
        )}
        {e911Live && (
          e911Live.ok ? (
            <div className="mt-3 rounded-md border border-green-200 bg-green-50/50 p-3 text-xs text-gray-700">
              <div className="font-medium text-green-800">Live from Commio{e911Live.registered ? ' · ERL registered' : ' · no ERL yet'}</div>
              <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Field label="Alias" value={e911Live.alias} />
                <Field label="ERL ID" value={e911Live.erl_id ? <span className="font-mono">{e911Live.erl_id}</span> : null} />
                <Field label="Address" value={e911Live.address} />
              </dl>
            </div>
          ) : (
            <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{e911Live.error}</p>
          )
        )}
      </Section>

      {/* Devices */}
      <Section
        title={devices.length > 1 ? `Devices (${devices.length})` : 'Devices'}
        subtitle={devices.length
          ? 'The equipment installed at this address. This is the address E911 dispatches to for each of them.'
          : 'No device at this address yet. Attach one from the subscriber or Devices page.'}
      >
        {devices.length > 0 && (
          <div className="mt-3 space-y-2">
            {devices.map((d) => (
              <div key={d.id} className="rounded-md border border-gray-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <Link to={`/devices/${d.id}`} className="font-mono text-sm font-medium text-blue-600 hover:underline">
                      {/* A Calix router has no MAC we manage — it's known by FSAN. */}
                      {d.mac || d.fsan || `#${d.id}`}
                    </Link>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {d.model || 'unknown model'} · {d.name || '(unnamed)'}
                      {d.device_type && d.device_type !== 'grandstream' && (
                        <span className="ml-1 text-gray-400">· {d.device_type}</span>
                      )}
                    </p>
                  </div>
                  {d.gdms_device_id && (
                    <span className="text-xs text-gray-500">GDMS #{d.gdms_device_id}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Modals */}
      {editing && (
        <LocationFormModal
          initial={loc}
          title="Edit Location"
          submitLabel="Save"
          onSubmit={(body) => patchLoc.mutate(body)}
          onCancel={() => { setEditing(false); patchLoc.reset(); }}
          isPending={patchLoc.isPending}
          error={patchLoc.isError ? (patchLoc.error?.body?.error || patchLoc.error?.message || 'Save failed') : null}
        />
      )}
    </div>
  );
}
